import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sun, Zap, Moon, Briefcase, MapPin, CreditCard, Clock, CheckCircle2 } from "lucide-react";
import useSeo from "../hooks/useSeo";

const SERVICES = [
  {
    id: "same_day",
    badge: "01",
    icon: Sun,
    name: "Same-Day Kurér",
    tagline: "Bestil i dag · leveret i dag",
    body: "Til pakker der skal afsted med det samme. Chaufføren henter direkte hos afsenderen og kører direkte videre til modtageren — same-day, ingen lager, ingen omlastning.",
    price: "Fra 199 kr",
    priceNote: "inkl. moms · inkl. 8 km · derefter +12 kr/km",
    accent: "border-brand-dark",
  },
  {
    id: "express",
    badge: "02",
    icon: Zap,
    name: "Express Kurér",
    tagline: "Levering inden 90 minutter",
    body: "Hasteopgaver hvor tid er kritisk. Direkte A→B-levering — vi kører fra afhentnings­adresse til leverings­adresse uden mellemstop. Dokumenter, prøver, reservedele.",
    price: "Fra 289 kr",
    priceNote: "inkl. moms · højeste prioritet",
    accent: "border-amber-500",
  },
  {
    id: "evening",
    badge: "03",
    icon: Moon,
    name: "Aftenlevering",
    tagline: "Afhentning & levering 18:00 – 22:00",
    body: "Aftenkurér for private og virksomheder der har brug for levering uden for normal arbejdstid. Vi henter pakken om aftenen og kører den direkte videre til modtageren samme aften.",
    price: "Fra 249 kr",
    priceNote: "inkl. moms · inkl. 8 km · derefter +12 kr/km",
    accent: "border-indigo-700",
  },
  {
    id: "business",
    badge: "04",
    icon: Briefcase,
    name: "Erhvervskurér + B2B platform",
    tagline: "Same Day · Next Day · evening · webshop og butikslevering",
    body: "Komplet B2B-leveringsløsning for webshops, butikker og virksomheder på Sjælland — med portal, CSV-import, labels, public tracking, pickup-manifests, failed-delivery flow og månedligt faktura-grundlag.",
    price: "Fra 0 kr/md",
    priceNote: "ekskl. moms · 89 kr pr. forsendelse · skalér når du er klar",
    accent: "border-slate-700",
  },
];

export default function ServicesPage() {
  useSeo({
    title: "Services — Same Day · Next Day · Aften · B2B platform · EMA Kurer ApS",
    description:
      "EMA Kurer's services: Same-Day kurér fra 199 kr inkl. moms, Express, Aftenlevering, og en komplet B2B-leveringsplatform med portal, CSV, labels, tracking og månedsfaktura. Hele Sjælland.",
    path: "/services",
  });
  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="bg-brand-dark text-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="kicker text-brand-red">Vores kurérservices</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-[1.05] max-w-3xl">
            Professionel kurérservice — <span className="text-brand-red">direkte fra A til B</span>.
          </h1>
          <p className="text-white/70 mt-6 text-lg max-w-2xl leading-relaxed">
            Vi er ikke et transportfirma. Vi er en kurérvirksomhed: Chaufføren henter pakken direkte hos dig og kører den direkte til modtageren — uden lager, terminal eller omlastning. Vælg den service der passer din situation.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 text-xs uppercase tracking-[0.18em] text-white/60">
            <span className="flex items-center gap-2"><CheckCircle2 size={14} className="text-brand-red" /> Direkte A → B</span>
            <span className="flex items-center gap-2"><CheckCircle2 size={14} className="text-brand-red" /> Ingen omlastning</span>
            <span className="flex items-center gap-2"><CheckCircle2 size={14} className="text-brand-red" /> Last-mile delivery</span>
            <span className="flex items-center gap-2"><CheckCircle2 size={14} className="text-brand-red" /> QR-baseret afhentning</span>
          </div>
        </div>
      </section>

      {/* 4 SERVICE CARDS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="services-grid">
        {SERVICES.map((s) => (
          <div key={s.id} className={`bg-white border ${s.accent} border-l-4 border-brand p-8 flex flex-col gap-4 hover:shadow-md transition-shadow`} data-testid={`service-card-${s.id}`}>
            <div className="flex items-start justify-between">
              <span className="font-display font-bold text-brand-red text-xs tracking-widest">{s.badge}</span>
              <s.icon className="text-brand-dark" size={26} />
            </div>
            <div className="font-display font-semibold text-2xl text-brand-dark mt-2">{s.name}</div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-brand-red font-semibold">{s.tagline}</div>
            <p className="text-brand-text2 leading-relaxed flex-1">{s.body}</p>
            <div className="mt-3 pt-5 border-t border-brand flex items-end justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-brand-text2">{s.price === "Tilbud" ? "Erhvervsaftale" : "Startpris"}</div>
                <div className="font-display font-bold text-2xl text-brand-dark mt-1">{s.price}</div>
                <div className="text-xs text-brand-text2 mt-1">{s.priceNote}</div>
              </div>
              {s.id === "business" ? (
                <Link to="/erhverv" className="btn-secondary text-xs py-2 px-4" data-testid={`cta-${s.id}`}>Se erhverv <ArrowRight size={14} /></Link>
              ) : (
                <Link to={`/book?type=${s.id}`} className="btn-primary text-xs py-2 px-4" data-testid={`cta-${s.id}`}>Book nu <ArrowRight size={14} /></Link>
              )}
            </div>
          </div>
        ))}
      </section>

      {/* PRICING TRANSPARENCY */}
      <section className="bg-brand-bg2 border-y border-brand py-20" data-testid="pricing-transparency">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="kicker">Pris-gennemsigtighed</div>
              <h2 className="font-display font-semibold text-3xl lg:text-4xl mt-3 text-brand-dark tracking-tight">Sådan beregner vi prisen.</h2>
              <p className="text-brand-text2 mt-5 leading-relaxed">
                Vi mener at du skal forstå <strong>hver eneste linje</strong> på din kvittering, før du betaler. Derfor viser vi altid hele udregningen åbent — fra startpris til total — med tillæg forklaret.
              </p>
              <Link to="/book" className="btn-primary mt-7 inline-flex" data-testid="pricing-cta-book">Se din pris live <ArrowRight size={16} /></Link>
            </div>
            <div className="lg:col-span-7">
              <div className="bg-white border border-brand p-7">
                <div className="text-[10px] tracking-[0.2em] uppercase text-brand-red font-semibold">Eksempel · Same-Day kurér</div>
                <div className="space-y-3 mt-5 text-sm">
                  <div className="flex justify-between"><span className="text-brand-text2">Startpris (inkl. 8 km)</span><span className="font-semibold text-brand-dark">199 kr</span></div>
                  <div className="flex justify-between pt-3 border-t border-brand">
                    <div>
                      <div className="text-brand-text2">Ekstra distance</div>
                      <div className="text-xs text-brand-text2/70 mt-0.5">15,5 km × 6 kr = 93 kr</div>
                    </div>
                    <span className="font-semibold text-brand-dark">+93 kr</span>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-brand">
                    <div>
                      <div className="text-amber-700 font-medium">Weekendtillæg</div>
                      <div className="text-xs text-amber-700/70 mt-0.5">+20% fordi leveringen er lørdag/søndag</div>
                    </div>
                    <span className="font-semibold text-amber-700">+58,40 kr</span>
                  </div>
                  <div className="flex justify-between pt-4 mt-3 border-t-2 border-brand-dark items-end">
                    <div>
                      <div className="text-[10px] tracking-widest uppercase text-brand-text2">Total inkl. 25% moms</div>
                      <div className="text-xs text-brand-text2 mt-0.5">Ingen skjulte gebyrer</div>
                    </div>
                    <span className="font-display font-bold text-3xl text-brand-dark">350,40 kr</span>
                  </div>
                </div>
                <div className="text-xs text-brand-text2 mt-5 border-t border-brand pt-4 leading-relaxed">
                  Tillæg påløber kun når det er relevant: <strong>Weekend +20%</strong> (lør/søn), <strong>Sen aften +15%</strong> (afhentning efter kl. 20). Ekstra pakker til samme adresse: +50 kr pr. stk.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* KURÉR VS FRAGT */}
      <section className="bg-white border-b border-brand py-20" data-testid="vs-freight">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="kicker">Direkte kurér — ikke fragt</div>
          <h2 className="font-display font-semibold text-3xl lg:text-4xl mt-3 text-brand-dark tracking-tight max-w-3xl">Vi er en direkte kurér — ikke pakkeservice som PostNord eller DAO.</h2>
          <p className="text-brand-text2 mt-5 max-w-3xl leading-relaxed">
            EMA Kurer henter pakken på din adresse og kører den direkte til modtageren. Leveringen går <strong>ikke</strong> via lager, terminal eller omlastning. Det er derfor du betaler for hastighed og direkte håndtering — ikke standard-pakkelevering.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-12">
            <div className="bg-emerald-50 border border-emerald-200 p-7">
              <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-700 font-bold">EMA Kurer · Direkte A → B</div>
              <ul className="mt-5 space-y-2.5 text-sm text-brand-dark">
                {[
                  "Vi henter pakken hjemme hos dig",
                  "Vi kører direkte til modtageren — ingen mellemstop",
                  "Ingen lager, ingen terminal, ingen omlastning",
                  "Leveret samme dag (same-day eller express)",
                  "Live GPS-tracking + push-besked",
                  "QR-bekræftet afhentning og levering",
                ].map((it) => (
                  <li key={it} className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-700 mt-0.5 flex-shrink-0" /> {it}</li>
                ))}
              </ul>
            </div>
            <div className="bg-slate-100 border border-slate-300 p-7">
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate-600 font-bold">Almindelig pakkelevering</div>
              <ul className="mt-5 space-y-2.5 text-sm text-slate-700">
                {[
                  "Du afleverer pakken i en pakkeshop",
                  "Pakken kører til centralt lager",
                  "Omlastes 1–2 gange undervejs",
                  "Levering 1–3 hverdage senere",
                  "Tracking opdateres typisk hver 4. time",
                  "Ingen direkte chauffør-kontakt",
                ].map((it) => (
                  <li key={it} className="flex gap-2 opacity-90"><span className="w-4 h-px bg-slate-400 mt-2.5 flex-shrink-0"></span> {it}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-brand-bg2 border-y border-brand py-20" data-testid="services-how-it-works">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="kicker">Sådan virker det</div>
          <h2 className="font-display font-semibold text-3xl lg:text-4xl mt-3 text-brand-dark tracking-tight">Fire trin — fra booking til levering</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mt-12">
            {[
              { n: "01", i: Clock, t: "Book online", d: "Vælg service, indtast afhentnings- og leveringsadresse, dine og modtagerens kontaktoplysninger." },
              { n: "02", i: CreditCard, t: "Betal sikkert", d: "Pris beregnes automatisk på km. Betal med kort eller MobilePay via Stripe." },
              { n: "03", i: MapPin, t: "QR-bekræftelse", d: "Modtag bookingbekræftelse med unik QR-kode. Vis den til chaufføren ved afhentning." },
              { n: "04", i: Zap, t: "Direkte til modtager", d: "Chaufføren scanner QR, henter pakken og kører direkte videre. Du følger leveringen live." },
            ].map((s) => (
              <div key={s.n} className="bg-white border border-brand p-7" data-testid={`step-${s.n}`}>
                <div className="flex items-center justify-between">
                  <span className="text-brand-red font-display font-bold text-2xl">{s.n}</span>
                  <s.i className="text-brand-dark" size={20} />
                </div>
                <div className="font-display font-semibold text-lg text-brand-dark mt-5">{s.t}</div>
                <div className="text-brand-text2 text-sm mt-2 leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/book" data-testid="services-page-cta" className="btn-primary">Book din kurér <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
